/**
 * Feb 2026 R293zze · Customer Overview
 *
 * A sales-focussed landing page for a single customer, linked from
 * every Quotation / SO / Invoice list by clicking the customer name.
 *
 * Tabs
 *   • Overview    — Receivables tiles + sales-pipeline intelligence
 *                   (quote win-rate, avg deal size, aging buckets,
 *                   avg days-to-pay).
 *   • Quotations  — Every estimate for this customer.
 *   • Sales Orders — Every sales_order for this customer.
 *   • Invoices    — Every invoice for this customer.
 *   • Payments    — Every recorded payment across invoices.
 *   • Ledger      — Chronological statement (invoice ↔ payment pairs,
 *                   running balance).
 *
 * Data pattern:
 *   • Client row loaded by `/clients/:cid` for the header.
 *   • GST docs filtered server-side via `/gst/workspaces/:ws/documents
 *     ?doc_type=…&client=<customer_name>` (regex, case-insensitive).
 *   • Receivables rollup already exists at
 *     `/gst/workspaces/:ws/receivables?customer_name=…`.
 *
 * Why a separate page (not just more tabs on ClientDetail): matches
 * the user's request for a focussed "money" view that's one click
 * away from a sales list, without diluting the broader Clients hub.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import {
  ArrowLeft, FileText, ShoppingCart, Receipt, Wallet, BookOpen,
  Building2, TrendingUp, Target, Clock, Calendar,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ReceivablesTiles from "@/components/Client/ReceivablesTiles";
import { StatusPill } from "@/pages/app/gst-sales/shared";
import { fmtMoney } from "@/lib/currency";

// ─── Intelligence tile ─────────────────────────────────────────────
function Metric({ icon: Icon, label, value, sub, tone = "neutral", testid }) {
  const tones = {
    neutral: "bg-card border-border",
    good:    "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/40 dark:border-emerald-800",
    warn:    "bg-amber-50 border-amber-200 dark:bg-amber-950/40 dark:border-amber-800",
    bad:     "bg-rose-50 border-rose-200 dark:bg-rose-950/40 dark:border-rose-800",
  };
  return (
    <div className={`border rounded-lg p-3 ${tones[tone] || tones.neutral}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className="font-bold text-base sm:text-lg mt-1 truncate">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function DocTable({ rows, type, testid, onOpen }) {
  if (!rows?.length) {
    return (
      <div className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-6 text-center"
           data-testid={`${testid}-empty`}>
        No {type} yet for this customer.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto border border-border rounded-lg" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-[10px] uppercase tracking-widest text-muted-foreground">
          <tr>
            <th className="text-left px-3 py-2">Doc no</th>
            <th className="text-left px-3 py-2">Date</th>
            <th className="text-left px-3 py-2">Subject</th>
            <th className="text-right px-3 py-2">Total</th>
            {type !== "quotations" && <th className="text-right px-3 py-2">Paid</th>}
            <th className="text-left px-3 py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const t = r.totals || {};
            const grand = Number(t.grand_total || 0);
            const paid = Number(r.paid_total || 0);
            const ccy = r.currency || t.currency || "INR";
            return (
              <tr key={r.id}
                  className="border-t border-border hover:bg-primary/5 cursor-pointer"
                  onClick={() => onOpen?.(r)}
                  data-testid={`customer-doc-${r.id}`}>
                <td className="px-3 py-2 font-semibold font-mono">{r.doc_no}</td>
                <td className="px-3 py-2 text-muted-foreground">{r.doc_date}</td>
                <td className="px-3 py-2 max-w-[280px] truncate" title={r.subject || ""}>
                  {r.subject || <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">
                  {fmtMoney(grand, ccy)}
                </td>
                {type !== "quotations" && (
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                    {paid > 0 ? fmtMoney(paid, ccy) : "—"}
                  </td>
                )}
                <td className="px-3 py-2"><StatusPill status={r.status} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function CustomerOverview({ cid: cidProp } = {}) {
  const params = useParams();
  const cid = cidProp || params.cid;
  const navigate = useNavigate();
  const { activeWorkspaceId: ws } = useAuth();
  const [client, setClient] = useState(null);
  const [recv, setRecv] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [sos, setSos] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!cid || !ws) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [cR, rR] = await Promise.all([
          api.get(`/clients/${cid}`),
          // Pull receivables with a placeholder; retry below after name known.
          Promise.resolve(null),
        ]);
        if (cancelled) return;
        setClient(cR.data || null);
        const customerName = (cR.data?.name || "").trim();
        if (!customerName) { setLoading(false); return; }
        const docParams = (type) => ({
          params: { doc_type: type, client: customerName, limit: 200 },
        });
        const [recR, qR, soR, iR] = await Promise.all([
          api.get(`/gst/workspaces/${ws}/receivables?customer_name=${encodeURIComponent(customerName)}`),
          api.get(`/gst/workspaces/${ws}/documents`, docParams("estimate")),
          api.get(`/gst/workspaces/${ws}/documents`, docParams("sales_order")),
          api.get(`/gst/workspaces/${ws}/documents`, docParams("invoice")),
        ]);
        if (cancelled) return;
        setRecv(recR.data || null);
        setQuotes(qR.data?.items || []);
        setSos(soR.data?.items || []);
        setInvoices(iR.data?.items || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [cid, ws]);

  // ─── Derived pipeline intelligence ────────────────────────────
  const intel = useMemo(() => {
    const totalQuotes = quotes.length;
    const accepted = quotes.filter((q) => q.status === "accepted").length;
    const converted = quotes.filter(
      (q) => q.status === "accepted" || q.status === "superseded"
             || q.converted_to_sales_order_id || q.converted_to_invoice_id).length;
    const winRate = totalQuotes ? Math.round((converted / totalQuotes) * 100) : null;
    const quoteValues = quotes.map((q) => Number(q?.totals?.grand_total || 0)).filter((n) => n > 0);
    const avgDeal = quoteValues.length
      ? quoteValues.reduce((a, b) => a + b, 0) / quoteValues.length
      : null;
    const biggest = quoteValues.length ? Math.max(...quoteValues) : null;

    // Days-to-pay from paid invoices with payments[]
    const dtp = [];
    for (const inv of invoices) {
      if (inv.status !== "paid" || !inv.doc_date) continue;
      const pays = Array.isArray(inv.payments) ? inv.payments : [];
      if (!pays.length) continue;
      const lastPaid = pays.map((p) => p.paid_on).filter(Boolean).sort().pop();
      if (!lastPaid) continue;
      const d1 = new Date(inv.doc_date);
      const d2 = new Date(lastPaid);
      const days = Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
      if (days >= 0) dtp.push(days);
    }
    const avgDtp = dtp.length
      ? Math.round(dtp.reduce((a, b) => a + b, 0) / dtp.length)
      : null;

    // Aging buckets from unpaid invoices
    const today = new Date().toISOString().slice(0, 10);
    const bucket = { "0-30": 0, "31-60": 0, "61-90": 0, "90+": 0 };
    for (const inv of invoices) {
      if (["paid", "void", "cancelled", "draft"].includes(inv.status)) continue;
      const t = inv.totals || {};
      const grand = Number(t.grand_total || 0);
      const paid = Number(inv.paid_total || 0);
      const tds = Number(t.tds_amount || 0);
      const outstanding = Math.max(0, grand - paid - tds);
      if (outstanding <= 0) continue;
      const due = inv.due_date || inv.doc_date;
      if (!due || due >= today) continue;
      const days = Math.round(
        (new Date(today) - new Date(due)) / (1000 * 60 * 60 * 24));
      const b = days <= 30 ? "0-30" : days <= 60 ? "31-60"
              : days <= 90 ? "61-90" : "90+";
      bucket[b] += outstanding;
    }
    const agingTotal = Object.values(bucket).reduce((a, b) => a + b, 0);

    return {
      winRate, totalQuotes, converted, avgDeal, biggest, avgDtp,
      aging: bucket, agingTotal,
    };
  }, [quotes, invoices]);

  // ─── Flat payments list (across all invoices) ─────────────────
  const payments = useMemo(() => {
    const rows = [];
    for (const inv of invoices) {
      for (const p of (inv.payments || [])) {
        rows.push({
          ...p,
          invoice_id: inv.id,
          invoice_no: inv.doc_no,
          currency: inv.currency || inv?.totals?.currency || "INR",
        });
      }
    }
    rows.sort((a, b) => (b.paid_on || "").localeCompare(a.paid_on || ""));
    return rows;
  }, [invoices]);

  // ─── Ledger: invoices (debit) interleaved with payments (credit) ──
  const ledger = useMemo(() => {
    const rows = [];
    for (const inv of invoices) {
      if (["void", "cancelled", "draft"].includes(inv.status)) continue;
      rows.push({
        date: inv.doc_date,
        type: "invoice",
        ref: inv.doc_no,
        subject: inv.subject,
        debit: Number(inv?.totals?.grand_total || 0),
        credit: 0,
        currency: inv.currency || inv?.totals?.currency || "INR",
        id: inv.id,
      });
      for (const p of (inv.payments || [])) {
        rows.push({
          date: p.paid_on || inv.doc_date,
          type: "payment",
          ref: `${inv.doc_no} · ${(p.mode || "").toUpperCase()}`,
          subject: p.reference || p.notes || "",
          debit: 0,
          credit: Number(p.amount || 0),
          currency: inv.currency || inv?.totals?.currency || "INR",
          id: p.id,
        });
      }
      // TDS as a settlement credit for display continuity
      const tds = Number(inv?.totals?.tds_amount || 0);
      if (tds > 0) {
        rows.push({
          date: inv.doc_date, type: "tds", ref: `${inv.doc_no} · TDS`,
          subject: `${inv?.tds_rate || 0}% withheld`,
          debit: 0, credit: tds,
          currency: inv.currency || inv?.totals?.currency || "INR",
          id: `${inv.id}-tds`,
        });
      }
    }
    rows.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    let bal = 0;
    for (const r of rows) {
      bal += r.debit - r.credit;
      r.balance = Math.round(bal * 100) / 100;
    }
    return rows;
  }, [invoices]);

  const openDoc = (doc) => {
    // Jump into the Sales page with the doc preselected via URL.
    navigate(`/app/sales?t=${doc.doc_type}&open=${doc.id}`);
  };

  if (loading && !client) {
    return (
      <div className="px-4 sm:px-6 md:px-10 pt-6">
        <div className="text-sm text-muted-foreground">Loading customer…</div>
      </div>
    );
  }
  if (!client) {
    return (
      <div className="px-4 sm:px-6 md:px-10 pt-6">
        <div className="text-sm text-muted-foreground">Customer not found.</div>
      </div>
    );
  }

  return (
    <>
      <div className="px-4 sm:px-6 md:px-10 pt-6">
        <Link to="/app/sales" className="text-sm label-eyebrow inline-flex items-center hover:text-primary"
              data-testid="customer-back-link">
          <ArrowLeft className="w-3 h-3 mr-1" /> Back to sales
        </Link>
      </div>
      <PageHeader
        eyebrow={["Customer", client.industry].filter(Boolean).join(" · ")}
        title={<span className="inline-flex items-center gap-2">{client.name}</span>}
        description={[client.company_size && `${client.company_size} employees`, client.website].filter(Boolean).join(" · ")
          || "Sales, receivables, payments, and ledger for this customer."}
        testid="customer-overview-header"
        actions={
          <Link to={`/app/clients/${cid}`}
                className="text-xs uppercase tracking-widest font-semibold text-primary hover:underline inline-flex items-center gap-1"
                data-testid="customer-full-profile-link">
            <Building2 className="w-3.5 h-3.5" /> Full client profile
          </Link>
        }
      />
      <div className="px-4 sm:px-6 md:px-10 py-6">
        {/* Receivables tiles — identical rollup used across the app */}
        <div className="mb-5" data-testid="customer-receivables-wrap">
          <ReceivablesTiles ws={ws} customerName={client.name} />
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="rounded-none">
            <TabsTrigger value="overview" className="rounded-none" data-testid="customer-tab-overview">
              <TrendingUp className="w-3.5 h-3.5 mr-1.5" /> Overview
            </TabsTrigger>
            <TabsTrigger value="quotations" className="rounded-none" data-testid="customer-tab-quotations">
              <FileText className="w-3.5 h-3.5 mr-1.5" /> Quotations · {quotes.length}
            </TabsTrigger>
            <TabsTrigger value="sales_orders" className="rounded-none" data-testid="customer-tab-sales-orders">
              <ShoppingCart className="w-3.5 h-3.5 mr-1.5" /> Sales Orders · {sos.length}
            </TabsTrigger>
            <TabsTrigger value="invoices" className="rounded-none" data-testid="customer-tab-invoices">
              <Receipt className="w-3.5 h-3.5 mr-1.5" /> Invoices · {invoices.length}
            </TabsTrigger>
            <TabsTrigger value="payments" className="rounded-none" data-testid="customer-tab-payments">
              <Wallet className="w-3.5 h-3.5 mr-1.5" /> Payments · {payments.length}
            </TabsTrigger>
            <TabsTrigger value="ledger" className="rounded-none" data-testid="customer-tab-ledger">
              <BookOpen className="w-3.5 h-3.5 mr-1.5" /> Ledger
            </TabsTrigger>
          </TabsList>

          {/* ─── Overview ─────────────────────────────────────── */}
          <TabsContent value="overview" className="pt-6 space-y-6">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
                Sales pipeline intelligence
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                <Metric
                  icon={Target}
                  label="Quote win rate"
                  value={intel.winRate != null ? `${intel.winRate}%` : "—"}
                  sub={intel.totalQuotes ? `${intel.converted} of ${intel.totalQuotes} quotes` : "No quotations yet"}
                  tone={intel.winRate == null ? "neutral"
                       : intel.winRate >= 60 ? "good"
                       : intel.winRate >= 30 ? "warn" : "bad"}
                  testid="metric-win-rate"
                />
                <Metric
                  icon={TrendingUp}
                  label="Avg deal size"
                  value={intel.avgDeal != null ? fmtMoney(intel.avgDeal, "INR") : "—"}
                  sub={intel.biggest != null ? `Biggest: ${fmtMoney(intel.biggest, "INR")}` : ""}
                  testid="metric-avg-deal"
                />
                <Metric
                  icon={Clock}
                  label="Avg days to pay"
                  value={intel.avgDtp != null ? `${intel.avgDtp} days` : "—"}
                  sub="From invoice date to final payment"
                  tone={intel.avgDtp == null ? "neutral"
                       : intel.avgDtp <= 30 ? "good"
                       : intel.avgDtp <= 60 ? "warn" : "bad"}
                  testid="metric-avg-dtp"
                />
                <Metric
                  icon={Calendar}
                  label="Overdue (aged)"
                  value={fmtMoney(intel.agingTotal, "INR")}
                  sub={intel.agingTotal > 0
                       ? `${fmtMoney(intel.aging["90+"], "INR")} is 90+ days`
                       : "All clear"}
                  tone={intel.agingTotal > 0 ? (intel.aging["90+"] > 0 ? "bad" : "warn") : "good"}
                  testid="metric-overdue"
                />
              </div>
            </div>

            {intel.agingTotal > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
                  Aging buckets
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {["0-30", "31-60", "61-90", "90+"].map((b) => (
                    <div key={b}
                         className="border border-border rounded-lg p-3"
                         data-testid={`aging-bucket-${b}`}>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                        {b} days
                      </div>
                      <div className="font-bold mt-1 tabular-nums">
                        {fmtMoney(intel.aging[b], "INR")}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {recv && (recv.quoted_sent > 0 || recv.quoted_accepted > 0) && (
              <div className="text-xs text-muted-foreground">
                <b>Pipeline:</b> {fmtMoney(recv.quoted_sent)} awaiting response ·{" "}
                {fmtMoney(recv.quoted_accepted)} accepted but not yet invoiced.
              </div>
            )}
          </TabsContent>

          {/* ─── Quotations ────────────────────────────────────── */}
          <TabsContent value="quotations" className="pt-6">
            <DocTable rows={quotes} type="quotations" testid="customer-quotations" onOpen={openDoc} />
          </TabsContent>

          {/* ─── Sales Orders ──────────────────────────────────── */}
          <TabsContent value="sales_orders" className="pt-6">
            <DocTable rows={sos} type="sales orders" testid="customer-sales-orders" onOpen={openDoc} />
          </TabsContent>

          {/* ─── Invoices ──────────────────────────────────────── */}
          <TabsContent value="invoices" className="pt-6">
            <DocTable rows={invoices} type="invoices" testid="customer-invoices" onOpen={openDoc} />
          </TabsContent>

          {/* ─── Payments ──────────────────────────────────────── */}
          <TabsContent value="payments" className="pt-6">
            {payments.length === 0 ? (
              <div className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-6 text-center"
                   data-testid="customer-payments-empty">
                No payments recorded yet.
              </div>
            ) : (
              <div className="overflow-x-auto border border-border rounded-lg" data-testid="customer-payments">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-[10px] uppercase tracking-widest text-muted-foreground">
                    <tr>
                      <th className="text-left px-3 py-2">Paid on</th>
                      <th className="text-left px-3 py-2">Invoice</th>
                      <th className="text-left px-3 py-2">Mode</th>
                      <th className="text-left px-3 py-2">Reference</th>
                      <th className="text-right px-3 py-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id}
                          className="border-t border-border hover:bg-primary/5"
                          data-testid={`customer-payment-${p.id}`}>
                        <td className="px-3 py-2 text-muted-foreground">{p.paid_on || "—"}</td>
                        <td className="px-3 py-2 font-mono">{p.invoice_no}</td>
                        <td className="px-3 py-2 uppercase">{p.mode}</td>
                        <td className="px-3 py-2 text-muted-foreground max-w-[320px] truncate"
                            title={p.reference || ""}>
                          {p.reference || "—"}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700 dark:text-emerald-300">
                          {fmtMoney(p.amount, p.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* ─── Ledger ────────────────────────────────────────── */}
          <TabsContent value="ledger" className="pt-6">
            {ledger.length === 0 ? (
              <div className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-6 text-center"
                   data-testid="customer-ledger-empty">
                No ledger entries yet.
              </div>
            ) : (
              <div className="overflow-x-auto border border-border rounded-lg" data-testid="customer-ledger">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-[10px] uppercase tracking-widest text-muted-foreground">
                    <tr>
                      <th className="text-left px-3 py-2">Date</th>
                      <th className="text-left px-3 py-2">Reference</th>
                      <th className="text-left px-3 py-2">Narration</th>
                      <th className="text-right px-3 py-2">Debit</th>
                      <th className="text-right px-3 py-2">Credit</th>
                      <th className="text-right px-3 py-2">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.map((r) => (
                      <tr key={r.id} className="border-t border-border hover:bg-primary/5"
                          data-testid={`customer-ledger-${r.id}`}>
                        <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.date || "—"}</td>
                        <td className="px-3 py-2 font-mono whitespace-nowrap">{r.ref}</td>
                        <td className="px-3 py-2 text-muted-foreground max-w-[320px] truncate"
                            title={r.subject || ""}>
                          {r.subject || <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {r.debit > 0 ? fmtMoney(r.debit, r.currency) : ""}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-emerald-700 dark:text-emerald-300">
                          {r.credit > 0 ? fmtMoney(r.credit, r.currency) : ""}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold">
                          {fmtMoney(r.balance, r.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
